import {useCallback} from 'react';
import {Connection} from 'reactflow';
import {v4 as uuidv4} from 'uuid';

import * as analytics from 'analytics/Analytics';
import {ActionOrigin, WORKFLOWS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import type {FlowchartEditorMode} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorModeConstants';

import {generateDeterministicEdgeId} from '../buildStaticDataFromFlowchartConfigGraphQL';
import {getEdgeTypeFromHandleId} from '../flowchartIdentifierUtils';
import {
  FlowchartStaticEdgeData,
  ProcessFlowchartEdgeType,
  StaticTemplateFlowchart,
} from '../processFlowchartConfigEditorConstants';
import {FlowchartEdgeChangeType} from '../processFlowchartConfigEditorUtils';
import {getOptionalTransitionsForNewEdge, shouldAddEdges} from '../types/transition/transitionLifecycleUtils';
import {Transition} from '../types/transition/TransitionTypes';
import {ApplyFlowchartChangesFn} from './FlowchartStateTypes';
import {trackOptionalTransitionPromotedAnalytics} from './useFlowchartStateAnalytics';

interface UseOnConnectParams {
  applyFlowchartChanges: ApplyFlowchartChangesFn;
  staticFlowchart: StaticTemplateFlowchart;
  transitions: Record<string, Transition>;
  flowchartTypeName: string;
  editorMode: FlowchartEditorMode;
  disallowTransitionEdits: boolean;
}

/**
 * Builds the `onConnect` callback fired by ReactFlow when the user draws a new
 * edge. Determines the edge type from source/target handles, gates the add on
 * `shouldAddEdges` (which may prompt for optional-transition promotion), and
 * tracks analytics.
 */
export function useOnConnect({
  applyFlowchartChanges,
  staticFlowchart,
  transitions,
  flowchartTypeName,
  editorMode,
  disallowTransitionEdits,
}: UseOnConnectParams): (connection: Connection) => Promise<void> {
  return useCallback(
    async (connection: Connection): Promise<void> => {
      if (
        connection.source == null ||
        connection.target == null ||
        connection.sourceHandle == null ||
        connection.targetHandle == null
      ) {
        return;
      }
      const {source, target, sourceHandle, targetHandle} = connection;

      const sourceHandleType = getEdgeTypeFromHandleId(sourceHandle);
      const targetHandleType = getEdgeTypeFromHandleId(targetHandle);

      const edgeType =
        sourceHandleType === targetHandleType ? sourceHandleType : ProcessFlowchartEdgeType.CONTROL_FLOW;

      analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.EDGE_DRAWN, {
        actionOrigin: ActionOrigin.WORKFLOWS_FLOWCHART_FIRST_CONFIGURATION_CANVAS,
        edgeType,
        flowchartType: flowchartTypeName,
        editorMode,
      });

      const newStaticEdge: FlowchartStaticEdgeData = {
        id:
          edgeType === ProcessFlowchartEdgeType.CONTROL_FLOW
            ? generateDeterministicEdgeId(source, target)
            : uuidv4(),
        source,
        target,
        sourceHandle,
        targetHandle,
        type: edgeType,
      };

      if (
        disallowTransitionEdits ||
        (await shouldAddEdges({
          edges: [newStaticEdge],
          staticFlowchart,
          transitions,
          flowchartTypeName,
        }))
      ) {
        if (!disallowTransitionEdits) {
          trackOptionalTransitionPromotedAnalytics(
            getOptionalTransitionsForNewEdge(
              newStaticEdge,
              staticFlowchart.nodesStaticData,
              [...staticFlowchart.edgesStaticData, newStaticEdge],
              transitions
            ),
            flowchartTypeName
          );
        }
        applyFlowchartChanges(() => ({
          edgeChanges: [{type: FlowchartEdgeChangeType.ADD, item: newStaticEdge}],
        }));
      }
    },
    [
      applyFlowchartChanges,
      disallowTransitionEdits,
      editorMode,
      flowchartTypeName,
      staticFlowchart,
      transitions,
    ]
  );
}
